import React, { useEffect, useState, useCallback } from 'react';

import { useImageConverter, useFileType } from '../services/hooks';
import { FilePreviewWithZoom } from '../FilePreviewWithZoom';
import { useIsDesktop } from '../hooks';
import { Dialog } from '../Dialog';

import { useStyles } from './FilePreviewWrapper.styles';
import { SpacingParams, FilePreviewProps } from './FilePreview.types';
import { FilePreviewContent } from './FilePreviewContent';
import { Error } from './FilePreviewError';
import { Loader } from './FilePreviewLoader';

const defaultSpasing: SpacingParams = {};

export const FilePreviewWrapper = (props: FilePreviewProps) => {
  const {
    onLoad,
    onError,
    clickable = false,
    onClickOpen,
    onClickClose,
    onLoadSuccess,
    showSnackbarOnDesktop = false,
    hideLoader = false,
    withZoom = false,
    defaultOpened = false,
    fullScreen = false,
    closeIcon = null,
    withoutError = false,
    mt = defaultSpasing,
    mb = defaultSpasing,
  } = props;
  const { classes } = useStyles({ mt, mb });

  const isDesktop = useIsDesktop();
  const imageConverter = useImageConverter();
  const fileTypeService = useFileType();

  const [error, setError] = useState(false);
  const [blobType, setBlobType] = useState<string>();
  const [objectUrl, setObjectUrl] = useState<string>('');
  const [isDialogOpened, setIsDialogOpened] = useState(defaultOpened);

  const handleOpen = useCallback(() => {
    onClickOpen?.();
    setIsDialogOpened(true);
  }, [onClickOpen]);

  const handleClose = useCallback(() => {
    setIsDialogOpened(false);
    onClickClose?.();
  }, [onClickClose]);

  useEffect(() => {
    let isCancelled = false;
    let nextObjectUrl = '';

    const loadFile = async () => {
      try {
        let file = await onLoad();
        const fileType = await fileTypeService.fileTypeFromBlob(file);

        if (
          [
            'image/heic',
            'image/heif',
            'image/tiff',
            'image/dng',
            'image/x-adobe-dng',
          ].includes(fileType)
        ) {
          file = await imageConverter.convert(file);
        }

        if (!isCancelled) {
          setBlobType(fileType);
          nextObjectUrl = URL.createObjectURL(file);
          setObjectUrl(nextObjectUrl);
          setError(false);

          if (onError) {
            onError(undefined);
          }
        }
      } catch (e) {
        if (!isCancelled) {
          setError(true);

          if (onError) {
            onError(e as Error);
          }
        }
      }
    };

    loadFile();

    return () => {
      isCancelled = true;

      if (nextObjectUrl) {
        URL.revokeObjectURL(nextObjectUrl);
      }
    };
  }, [onLoad, imageConverter, onError, fileTypeService]);

  if (error) {
    if (defaultOpened)
      return (
        <Dialog onClose={handleClose} open fullScreen={!isDesktop}>
          <Error withoutError={withoutError} />
        </Dialog>
      );

    return <Error withoutError={withoutError} />;
  }

  if (objectUrl === null || !blobType) {
    if (hideLoader) {
      return null;
    }

    if (defaultOpened)
      return (
        <Dialog onClose={handleClose} open fullScreen={!isDesktop}>
          <Loader />
        </Dialog>
      );

    return <Loader />;
  }

  if (defaultOpened) {
    const hasZoom =
      withZoom &&
      (blobType === 'application/pdf' || blobType?.startsWith('image/'));

    if (hasZoom) {
      return (
        <FilePreviewWithZoom
          onClose={handleClose}
          open={isDialogOpened}
          url={objectUrl}
          onLoadSuccess={onLoadSuccess}
          withBottomOffset={showSnackbarOnDesktop}
          fileType={blobType === 'application/pdf' ? 'pdf' : 'image'}
        />
      );
    }

    return (
      <Dialog
        scroll="body"
        onClose={handleClose}
        open={isDialogOpened}
        fullScreen={fullScreen}
        withoutRadius={fullScreen}
        className="aqa_file_preview_dialog"
        classes={{
          paper: classes.paper,
        }}
      >
        <FilePreviewContent
          {...props}
          blobType={blobType}
          objectUrl={objectUrl}
          handleOpen={handleOpen}
          handleClose={handleClose}
          clickable={clickable}
          open={isDialogOpened}
        />
        {closeIcon}
      </Dialog>
    );
  }

  return (
    <FilePreviewContent
      {...props}
      blobType={blobType}
      objectUrl={objectUrl}
      handleOpen={handleOpen}
      handleClose={handleClose}
      clickable={clickable}
      open={isDialogOpened}
    />
  );
};
